import { useContext, useState, useEffect } from 'react';
import { AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Mail, Shield, Phone, Calendar, Globe, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileTab = () => {
  const { user, login } = useContext(AuthContext); // login acts as 'updateUser' if we pass new data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dob: '',
    subscription_status: '',
    proxy_ip: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        dob: user.dob || '',
        subscription_status: user.subscription_status || 'Inactive',
        proxy_ip: user.proxy_ip || 'Not Assigned'
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { data } = await axios.put(`${API}/auth/me`, {
        name: formData.name,
        phone: formData.phone,
        dob: formData.dob
      }, { withCredentials: true });

      // Update context
      // We assume AuthContext might re-fetch or we manually check. 
      // Ideally we trigger a reload or update state if AuthContext exposes a setter.
      // For now, simple toast.
      toast.success('Profile updated successfully');
      setIsEditing(false);
    } catch (error) {
      toast.error('Failed to update profile');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-8" data-testid="profile-tab-content">
      {/* Left Col: Identity Card */}
      <Card className="p-8 bg-white/80 backdrop-blur-sm border-gray-200 lg:col-span-1 h-fit">
        <div className="flex flex-col items-center text-center">
          <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
            <span className="text-white text-4xl font-bold">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">{user?.name}</h2>
          <p className="text-gray-500 mb-4">{user?.email}</p>

          <div className="flex items-center gap-2 px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium">
            <Shield className="w-4 h-4" />
            {user?.role === 'admin' ? 'Administrator' : 'Client Account'}
          </div>
        </div>

        <div className="mt-8 space-y-4">
          <div className="p-4 bg-slate-50 rounded-xl border border-gray-200">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">My Static IP</h4>
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-500" />
              <span className="text-lg font-mono font-bold text-gray-900">{formData.proxy_ip}</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">Whitelist this IP in your Zebu settings.</p>
          </div>

          <div className="p-4 bg-slate-50 rounded-xl border border-gray-200">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Subscription</h4>
            <div className="flex items-center gap-2">
              {formData.subscription_status.toLowerCase() === 'active' ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <AlertCircle className="w-5 h-5 text-orange-500" />
              )}
              <span className="text-lg font-bold text-gray-900 capitalize">{formData.subscription_status}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Right Col: Details Form */}
      <Card className="p-8 bg-white/80 backdrop-blur-sm border-gray-200 lg:col-span-2">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">Personal Details</h3>
          <Button
            variant={isEditing ? "ghost" : "outline"}
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? 'Cancel' : 'Edit Profile'}
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  name="email"
                  value={formData.email}
                  disabled
                  className="pl-9 bg-gray-50"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="pl-9"
                  placeholder="+91 9876543210"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Date of Birth (DD-MM-YYYY)</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  name="dob"
                  value={formData.dob}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="pl-9"
                  placeholder="01-01-1990"
                />
              </div>
              <p className="text-xs text-gray-500">Required for Zebu API Login.</p>
            </div>
          </div>

          {isEditing && (
            <div className="flex justify-end pt-4">
              <Button type="submit" disabled={isLoading} className="bg-indigo-600 hover:bg-indigo-700">
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
};

export default ProfileTab;
